import { uniq } from 'es-toolkit';
import { create } from 'zustand';

export type RelationshipType = 'mutual' | 'following' | 'followed_by' | 'none';

export type RelationshipStatus = {
  type: RelationshipType;
  label: string;
  shortLabel: string;
  statusMask: number;
  isMutual: boolean;
};

type RelationshipsState = {
  known: Record<string, RelationshipStatus>;
  requested: Record<string, true>;
  pendingQueue: string[];
  queueInitial: (ids: string[]) => void;
  ensure: (profileId: string) => void;
  takePending: () => string[];
  setKnownBatch: (entries: Record<string, RelationshipStatus>) => void;
};

const readRecordValue = <T>(record: Record<string, T>, key: string): T | undefined =>
  Object.hasOwn(record, key) ? record[key] : undefined;

const writeRecordValue = <T>(record: Record<string, T>, key: string, value: T): Record<string, T> => {
  const next = { ...record };
  Object.defineProperty(next, key, {
    value,
    writable: true,
    enumerable: true,
    configurable: true,
  });
  return next;
};

const normalizeIds = (ids: string[]): string[] => {
  if (ids.length === 0) {
    return [];
  }
  return uniq(ids.filter((id): id is string => typeof id === 'string' && id.length > 0));
};

export const useRelationshipsStore = create<RelationshipsState>()((set, get) => ({
  known: {},
  requested: {},
  pendingQueue: [],
  queueInitial: (ids: string[]) => {
    const normalized = normalizeIds(ids);
    if (normalized.length === 0) {
      return;
    }
    set((state) => {
      const nextRequested: Record<string, true> = { ...state.requested };
      const nextQueue = state.pendingQueue.length > 0 ? [...state.pendingQueue] : [];
      let requestedChanged: boolean | undefined;
      let queueChanged: boolean | undefined;

      for (const id of normalized) {
        if (readRecordValue(state.known, id) || readRecordValue(nextRequested, id)) {
          continue;
        }
        Object.defineProperty(nextRequested, id, {
          value: true as const,
          writable: true,
          enumerable: true,
          configurable: true,
        });
        requestedChanged = true;
        nextQueue.push(id);
        queueChanged = true;
      }

      const hasPendingChanges = Boolean(requestedChanged || queueChanged);
      if (!hasPendingChanges) {
        return state;
      }

      return {
        requested: nextRequested,
        pendingQueue: nextQueue,
      };
    });
  },
  ensure: (profileId: string) => {
    if (profileId === undefined) {
      return;
    }
    set((state) => {
      if (readRecordValue(state.known, profileId) || readRecordValue(state.requested, profileId)) {
        return state;
      }
      const nextRequested = writeRecordValue(state.requested, profileId, true as const);
      const nextQueue = state.pendingQueue.length > 0 ? [...state.pendingQueue, profileId] : [profileId];
      return {
        requested: nextRequested,
        pendingQueue: nextQueue,
      } satisfies Partial<RelationshipsState>;
    });
  },
  takePending: () => {
    const state = get();
    const pendingQueue = Array.isArray(state.pendingQueue) ? state.pendingQueue : [];
    if (pendingQueue.length === 0) {
      return [];
    }
    const normalized = normalizeIds(pendingQueue);
    set({ pendingQueue: [] });
    return normalized;
  },
  setKnownBatch: (entries: Record<string, RelationshipStatus>) => {
    const ids = Object.keys(entries);
    if (ids.length === 0) {
      return;
    }
    set((state) => {
      let changed = false;
      let next = state.known;
      for (const id of ids) {
        const status = readRecordValue(entries, id);
        if (!status) {
          continue;
        }
        const existing = readRecordValue(next, id);
        if (existing === status) {
          continue;
        }
        if (next === state.known) {
          next = { ...state.known };
        }
        Object.defineProperty(next, id, {
          value: status,
          writable: true,
          enumerable: true,
          configurable: true,
        });
        changed = true;
      }
      if (!changed) {
        return state;
      }
      return { known: next } satisfies Partial<RelationshipsState>;
    });
  },
}));

export const resetRelationshipsStore = () => {
  useRelationshipsStore.setState({
    known: {},
    requested: {},
    pendingQueue: [],
  });
};
